import React, { useState, useEffect, useRef, KeyboardEvent, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, MessageSquare, Link2, Mail, Phone, Check, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import api from '@/services/api';
import { Client as TwilioClient, Conversation as TwilioConversation, Message as TwilioMessage, Participant } from '@twilio/conversations';
import { API_SERVER_URL } from '@/lib/apiConfig';

const API_BASE_URL = API_SERVER_URL;

interface Conversation {
  /** New patient-messaging module uses `id`; legacy used `conversationId` */
  id: string;
  conversationId?: string;
  twilioSid?: string;
  type: string;
  status: string;
  createdAt: string;
  clinicId: string;
  lastMessageAt?: string;
  patientId?: string;
  patientName?: string;
  patientEmail?: string;
  patientPhone?: string;
  /** True when the provider joined an existing shared conversation */
  joined?: boolean;
}

interface Message {
  messageId: string;
  body: string;
  authorId: string;
  authorType?: string;
  senderName?: string;
  senderRole?: string;
  createdAt: string;
  conversationId: string;
}

/** Helper: get the canonical conversation ID (works for both old and new API shapes) */
function getConvId(conv: Conversation): string {
  return conv.id || conv.conversationId || '';
}

interface PatientMessagingTabProps {
  patientId?: string;
  patientName: string;
  patientEmail?: string;
  patientPhone?: string;
  isActive?: boolean;
}

const PatientMessagingTab: React.FC<PatientMessagingTabProps> = ({
  patientId,
  patientName,
  patientEmail,
  patientPhone,
  isActive = true,
}) => {
  const { user } = useAuth();
  const { clinic, isPractitioner, isAdmin } = useClinic();
  const { toast } = useToast();

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [creatingConversation, setCreatingConversation] = useState(false);

  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected' | 'failed' | 'error'>('disconnected');
  const isConnected = connectionStatus === 'connected';
  const [typingParticipants, setTypingParticipants] = useState<string[]>([]);
  const twilioClientRef = useRef<TwilioClient | null>(null);
  const twilioConvRef = useRef<TwilioConversation | null>(null);
  const twilioUnsubscribeRef = useRef<(() => void) | null>(null);

  const [showSendLinkDialog, setShowSendLinkDialog] = useState(false);
  const [sendLinkChannel, setSendLinkChannel] = useState<'email' | 'sms' | 'both'>('email');
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const getConnectionStatusDisplay = useCallback((status: string) => {
    switch (status) {
      case 'connected':
        return { label: 'Connected', color: 'bg-emerald-500', icon: Wifi };
      case 'connecting':
        return { label: 'Connecting...', color: 'bg-yellow-500', icon: Wifi };
      case 'disconnected':
      case 'failed':
      case 'error':
        return { label: 'Disconnected', color: 'bg-red-500', icon: WifiOff };
      default:
        return { label: 'Unknown', color: 'bg-gray-500', icon: Wifi };
    }
  }, []);

  const getInitials = (name?: string) => {
    if (!name) return 'P';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Deterministic color per authorId
  const userColors = [
    { bg: 'bg-teal-100', text: 'text-teal-700', name: 'text-teal-600', bubble: 'bg-teal-600 text-white' },
    { bg: 'bg-blue-100', text: 'text-blue-700', name: 'text-blue-600', bubble: 'bg-blue-600 text-white' },
    { bg: 'bg-purple-100', text: 'text-purple-700', name: 'text-purple-600', bubble: 'bg-purple-600 text-white' },
    { bg: 'bg-pink-100', text: 'text-pink-700', name: 'text-pink-600', bubble: 'bg-pink-500 text-white' },
    { bg: 'bg-orange-100', text: 'text-orange-700', name: 'text-orange-600', bubble: 'bg-orange-500 text-white' },
    { bg: 'bg-indigo-100', text: 'text-indigo-700', name: 'text-indigo-600', bubble: 'bg-indigo-600 text-white' },
    { bg: 'bg-emerald-100', text: 'text-emerald-700', name: 'text-emerald-600', bubble: 'bg-emerald-600 text-white' },
    { bg: 'bg-rose-100', text: 'text-rose-700', name: 'text-rose-600', bubble: 'bg-rose-500 text-white' },
  ];

  const getColorForAuthor = (authorId: string) => {
    let hash = 0;
    for (let i = 0; i < authorId.length; i++) {
      hash = ((hash << 5) - hash + authorId.charCodeAt(i)) | 0;
    }
    return userColors[Math.abs(hash) % userColors.length];
  };

  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    if (messages.length === 0) return;
    const isInitialLoad = prevMessageCountRef.current === 0;
    prevMessageCountRef.current = messages.length;
    if (isInitialLoad) {
      // Timeout ensures the flex layout has settled before scrolling
      setTimeout(() => {
        const el = messagesEndRef.current;
        if (el) {
          const container = el.closest('.overflow-y-auto.p-3') as HTMLElement;
          if (container) container.scrollTop = container.scrollHeight;
        }
      }, 100);
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Scroll to bottom when tab becomes active again (forceMount keeps component alive)
  useEffect(() => {
    if (isActive && messages.length > 0) {
      setTimeout(() => {
        const el = messagesEndRef.current;
        if (el) {
          const container = el.closest('.overflow-y-auto.p-3') as HTMLElement;
          if (container) container.scrollTop = container.scrollHeight;
        }
      }, 100);
    }
  }, [isActive]);

  // Cleanup Twilio client on unmount
  useEffect(() => {
    return () => {
      twilioClientRef.current?.shutdown();
      twilioClientRef.current = null;
      twilioConvRef.current = null;
    };
  }, []);

  const loadConversationForPatient = useCallback(async () => {
    if (!patientId || !clinic?.id) return;
    setLoading(true);
    try {
      // New patient-messaging module: shared inbox scoped by clinic
      const response = await api.get('/patient-messaging', {
        headers: { 'x-selected-clinic-id': clinic.id },
      });
      const allConversations: Conversation[] = response.data.conversations || [];
      const match = allConversations.find(
        (c) => c.patientId === patientId && c.status !== 'archived'
      );
      if (match) {
        setConversation(match);
      } else {
        setConversation(null);
      }
    } catch (error: any) {
      console.error('[PatientMessaging] Failed to load conversations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load messaging data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [patientId, clinic?.id, toast]);

  useEffect(() => {
    loadConversationForPatient();
  }, [loadConversationForPatient]);

  const loadMessages = async (conversationId: string) => {
    try {
      const response = await api.get(`/patient-messaging/${conversationId}/messages`, {
        headers: { 'x-selected-clinic-id': clinic?.id },
      });
      const loadedMessages: Message[] = (response.data.messages || []).map((msg: any) => ({
        messageId: msg.id || msg.messageId,
        body: msg.body || '',
        authorId: msg.author_id || msg.authorId || '',
        authorType: msg.author_type || msg.authorType || '',
        senderName: msg.sender_name || msg.senderName || '',
        senderRole: msg.sender_role || msg.senderRole || '',
        createdAt: msg.created_at || msg.createdAt || '',
        conversationId,
      }));
      const sorted = loadedMessages.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setMessages(sorted);
    } catch (error) {
      console.error('[PatientMessaging] Failed to load messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (!conversation?.twilioSid) {
      if (conversation) {
        setLoadingMessages(true);
        loadMessages(getConvId(conversation));
      }
      return;
    }

    // Always load messages from REST first
    setLoadingMessages(true);
    loadMessages(getConvId(conversation));

    if (!conversation.twilioSid) return;

    let cancelled = false;

    const setupRealtimeSubscription = async () => {
      try {
        setConnectionStatus('connecting');
        twilioUnsubscribeRef.current?.();

        // Ensure participant in Twilio (adds staff to Twilio conversation)
        await api.post(
          `/patient-messaging/${getConvId(conversation)}/ensure-participant`,
          {},
          { headers: { 'x-selected-clinic-id': conversation.clinicId || clinic?.id } },
        );

        // Get a Twilio token using the patient-messaging endpoint (no tenant)
        const tokenRes = await api.get('/patient-messaging/twilio-token', {
          headers: { 'x-selected-clinic-id': conversation.clinicId || clinic?.id },
        });
        const { token: twilioToken } = tokenRes.data;

        if (cancelled) return;

        // Create a local Twilio Client
        if (!twilioClientRef.current) {
          const client = new TwilioClient(twilioToken);
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Twilio init timeout')), 15000);
            client.on('stateChanged', (state: string) => {
              if (state === 'initialized') { clearTimeout(timeout); resolve(); }
              if (state === 'failed') { clearTimeout(timeout); reject(new Error('Twilio init failed')); }
            });
          });
          if (cancelled) { client.shutdown(); return; }
          twilioClientRef.current = client;
        } else {
          await twilioClientRef.current.updateToken(twilioToken);
        }

        // Subscribe to the conversation
        const conv = await twilioClientRef.current.getConversationBySid(conversation.twilioSid!);
        twilioConvRef.current = conv;

        if (cancelled) return;
        setConnectionStatus('connected');

        // Listen for new messages
        const onMessageAdded = (msg: TwilioMessage) => {
          // Extract sender info from Twilio message attributes
          let senderName = '';
          let senderRole = '';
          let authorType = '';
          try {
            const attrs = typeof msg.attributes === 'string'
              ? JSON.parse(msg.attributes)
              : (msg.attributes as Record<string, unknown>) || {};
            if (attrs.authorName) senderName = String(attrs.authorName);
            if (attrs.authorRole) senderRole = String(attrs.authorRole);
            if (attrs.authorType) authorType = String(attrs.authorType);
          } catch {
            // Attributes not parseable
          }

          // Determine authorType from identity if not in attributes
          const author = msg.author || '';
          if (!authorType) {
            authorType = author.includes('patient:') ? 'patient' : 'staff';
          }

          const newMsg: Message = {
            messageId: msg.sid,
            body: msg.body || '',
            authorId: author,
            authorType,
            senderName,
            senderRole,
            createdAt: msg.dateCreated?.toISOString() || new Date().toISOString(),
            conversationId: getConvId(conversation),
          };
          setMessages((prev) => {
            if (prev.some((m) => m.messageId === newMsg.messageId)) return prev;
            const optimisticIndex = prev.findIndex(
              (m) => m.messageId.startsWith('temp-') && m.body === newMsg.body
            );
            if (optimisticIndex !== -1) {
              const updated = [...prev];
              updated[optimisticIndex] = newMsg;
              return updated.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            }
            return [...prev, newMsg].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          });
        };
        conv.on('messageAdded', onMessageAdded);

        // Typing indicators
        const onTypingStarted = (p: Participant) => {
          const pid = p.identity || p.sid;
          setTypingParticipants((prev) => (prev.includes(pid) ? prev : [...prev, pid]));
        };
        const onTypingEnded = (p: Participant) => {
          const pid = p.identity || p.sid;
          setTypingParticipants((prev) => prev.filter((x) => x !== pid));
        };
        conv.on('typingStarted', onTypingStarted);
        conv.on('typingEnded', onTypingEnded);

        twilioUnsubscribeRef.current = () => {
          conv.removeListener('messageAdded', onMessageAdded);
          conv.removeListener('typingStarted', onTypingStarted);
          conv.removeListener('typingEnded', onTypingEnded);
        };
      } catch (error) {
        console.error('[PatientMessaging] Failed to setup real-time subscription:', error);
        if (!cancelled) setConnectionStatus('failed');
      }
    };

    setupRealtimeSubscription();

    return () => {
      cancelled = true;
      twilioUnsubscribeRef.current?.();
      twilioUnsubscribeRef.current = null;
    };
  }, [conversation?.twilioSid, conversation?.conversationId, clinic?.id]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessageHandler();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
    if (conversation?.twilioSid && isConnected && twilioConvRef.current) {
      twilioConvRef.current.typing();
    }
  };

  const sendMessageHandler = async () => {
    if (!conversation || !newMessage.trim()) return;

    setSendingMessage(true);
    const messageBody = newMessage.trim();
    setNewMessage('');

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Derive display role from clinic context flags (matches backend getSenderInfo)
    const roles: string[] = [];
    if (isPractitioner) roles.push('provider');
    if (isAdmin) roles.push('clinic admin');
    if (roles.length === 0 && false) roles.push('staff');
    const displayRole = roles.length > 0 ? roles.join(' + ') : 'staff';

    const tempMessageId = `temp-${Date.now()}`;
    const optimisticMessage: Message = {
      messageId: tempMessageId,
      body: messageBody,
      authorId: user?.id || '',
      authorType: 'staff',
      senderName: userName.trim() || 'Staff',
      senderRole: displayRole,
      createdAt: new Date().toISOString(),
      conversationId: getConvId(conversation),
    };

    setMessages((prev) =>
      [...prev, optimisticMessage].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
    );

    try {
      if (conversation.twilioSid && isConnected && twilioConvRef.current) {
        // Send via Twilio SDK for real-time delivery to patient (with sender attributes)
        const msgBuilder = twilioConvRef.current.prepareMessage();
        msgBuilder.setBody(messageBody);
        msgBuilder.setAttributes({
          authorName: userName.trim() || 'Staff',
          authorRole: displayRole,
          authorType: 'staff',
        });
        await msgBuilder.build().send();
        setMessages((prev) => prev.filter((m) => m.messageId !== tempMessageId));
        setSendingMessage(false);
        textareaRef.current?.focus();
        // Persist to PostgreSQL in the background so clinic-side queries stay in sync
        api.post(`/patient-messaging/${getConvId(conversation)}/messages`, {
          body: messageBody,
          skipTwilio: true, // Already sent via SDK — don't double-send
        }, {
          headers: { 'x-selected-clinic-id': clinic?.id },
        }).catch(persistError => {
          console.warn('[PatientMessaging] Failed to persist Twilio message to DB:', persistError);
        });
      } else {
        await api.post(`/patient-messaging/${getConvId(conversation)}/messages`, {
          body: messageBody,
        }, {
          headers: { 'x-selected-clinic-id': clinic?.id },
        });
        setMessages((prev) => prev.filter((m) => m.messageId !== tempMessageId));
        loadMessages(getConvId(conversation));
      }
    } catch (error: any) {
      console.error('[PatientMessaging] Failed to send message:', error);
      setMessages((prev) => prev.filter((m) => m.messageId !== tempMessageId));
      setNewMessage(messageBody);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.message || 'Failed to send message',
        variant: 'destructive',
      });
    } finally {
      setSendingMessage(false);
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  };

  // Creates a new conversation or joins the existing shared one for this
  // patient+clinic. The backend returns `joined: true` when an existing
  // conversation was found.
  const createConversationHandler = async () => {
    if (!patientId || !clinic?.id) return;

    setCreatingConversation(true);
    try {
      const response = await api.post('/patient-messaging', {
        clinicId: clinic.id,
        type: 'DIRECT',
        patientId,
        patientName,
        patientEmail,
        patientPhone,
      }, {
        headers: { 'x-selected-clinic-id': clinic.id },
      });

      const joined = response.data?.joined === true;
      toast({
        title: joined ? 'Joined conversation' : 'Success',
        description: joined
          ? `Opened existing conversation with ${patientName}`
          : `Conversation started with ${patientName}`,
      });

      if (response.data) {
        setConversation(response.data);
        setMessages([]);
        setLoadingMessages(true);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.message || 'Failed to create conversation',
        variant: 'destructive',
      });
    } finally {
      setCreatingConversation(false);
    }
  };

  const sendLinkToPatient = async () => {
    if (!conversation) return;

    setSendingLink(true);
    setLinkSent(false);

    try {
      const response = await api.post(
        `/patient-messaging/${getConvId(conversation)}/send-link`,
        { channel: sendLinkChannel },
        { headers: { 'x-selected-clinic-id': clinic?.id } },
      );

      if (response.data.success) {
        setLinkSent(true);
        toast({
          title: 'Link Sent',
          description: `Chat link sent via ${sendLinkChannel === 'both' ? 'email and SMS' : sendLinkChannel}.`,
        });
        setTimeout(() => {
          setShowSendLinkDialog(false);
          setLinkSent(false);
        }, 2000);
      } else {
        throw new Error(response.data.message || response.data.error || 'Failed to send link');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to send link',
        variant: 'destructive',
      });
    } finally {
      setSendingLink(false);
    }
  };

  const connectionDisplay = getConnectionStatusDisplay(connectionStatus);
  const ConnectionIcon = connectionDisplay.icon;

  const patientInitials = getInitials(patientName);
  const userName = (user?.firstName || '') + ' ' + (user?.lastName || '');
  const providerInitials =
    userName
      .split(' ')
      .filter((n) => n)
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'PR';

  if (loading) {
    return (
      <Card className="border border-slate-200">
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-brand-teal" />
        </CardContent>
      </Card>
    );
  }

  if (!conversation) {
    return (
      <Card className="border border-slate-200">
        <CardContent className="flex flex-col items-center justify-center py-20 text-center">
          <MessageSquare className="h-12 w-12 text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-1">No conversation yet</h3>
          <p className="text-sm text-slate-500 mb-6 max-w-sm">
            Start a secure messaging conversation with {patientName} to communicate directly from
            their chart.
          </p>
          <Button onClick={createConversationHandler} disabled={creatingConversation}>
            {creatingConversation ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <MessageSquare className="h-4 w-4 mr-2" />
                Start Conversation
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border border-slate-200 flex flex-col" style={{ height: 'calc(100vh - 320px)', minHeight: '500px' }}>
        <CardHeader className="border-b border-slate-100 pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-brand-teal/10 text-brand-teal text-xs font-semibold">
                  {patientInitials}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-sm font-semibold text-slate-900">
                  {patientName}
                </CardTitle>
                <CardDescription className="text-xs">
                  {patientEmail || patientPhone || 'Direct message'}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 bg-white text-[11px]">
                <div className={`w-1.5 h-1.5 rounded-full ${connectionDisplay.color}`} />
                <ConnectionIcon className="h-3 w-3 text-slate-500" />
                <span className="text-slate-600 font-medium">{connectionDisplay.label}</span>
              </div>
              {!isConnected && connectionStatus === 'failed' && (
                <Button variant="outline" size="sm" onClick={() => {
                  twilioClientRef.current?.shutdown();
                  twilioClientRef.current = null;
                  twilioConvRef.current = null;
                  setConnectionStatus('disconnected');
                }} className="h-7 text-xs">
                  Reconnect
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSendLinkDialog(true)}
                className="h-8 text-xs"
              >
                <Link2 className="h-3.5 w-3.5 mr-1.5" />
                Send Link
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setLoadingMessages(true);
                  {
                    loadMessages(getConvId(conversation));
                  }
                }}
                className="h-8 w-8 p-0"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Badge className="text-[10px]">{conversation.status}</Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 p-0 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-3">
            {loadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-brand-teal" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-400">
                <p className="text-sm">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg) => {
                  // Only current user's messages on the right
                  const isCurrentUser = msg.authorId === user?.id;

                  const color = getColorForAuthor(msg.authorId);

                  // Display name: patient shows name (patient), staff shows senderName (role)
                  const isPatient = msg.authorType === 'patient' || msg.authorId?.includes('patient:');
                  const displayName = isPatient
                    ? `${msg.senderName || patientName || 'Patient'} (patient)`
                    : `${msg.senderName || 'Staff'}${msg.senderRole ? ` (${msg.senderRole})` : ''}`;
                  const initials = isPatient
                    ? (msg.senderName ? getInitials(msg.senderName) : patientInitials)
                    : (msg.senderName ? getInitials(msg.senderName) : providerInitials);

                  return (
                    <div
                      key={msg.messageId}
                      className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`flex gap-2 max-w-[70%] ${isCurrentUser ? 'flex-row-reverse' : ''}`}
                      >
                        <Avatar className="h-7 w-7 flex-shrink-0">
                          <AvatarFallback
                            className={`${isCurrentUser ? 'bg-primary/10 text-primary' : `${color.bg} ${color.text}`} text-[10px] font-semibold`}
                          >
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p
                            className={`text-[11px] font-semibold mb-0.5 ${
                              isCurrentUser ? 'text-right text-primary' : color.name
                            }`}
                          >
                            {displayName}
                          </p>
                          <div
                            className={`rounded-lg px-3 py-2 text-sm ${
                              isCurrentUser
                                ? 'bg-primary text-primary-foreground rounded-br-sm'
                                : 'bg-slate-100 text-slate-900 rounded-bl-sm'
                            }`}
                          >
                            <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                          </div>
                          <p
                            className={`text-[11px] mt-1 text-slate-400 ${isCurrentUser ? 'text-right' : ''}`}
                          >
                            {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {typingParticipants.length > 0 && (
            <div className="px-3 py-1.5 text-xs text-slate-500 animate-pulse">
              <span className="inline-flex items-center gap-1">
                <span className="flex gap-0.5">
                  <span
                    className="w-1 h-1 bg-slate-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className="w-1 h-1 bg-slate-400 rounded-full animate-bounce"
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className="w-1 h-1 bg-slate-400 rounded-full animate-bounce"
                    style={{ animationDelay: '300ms' }}
                  />
                </span>
                <span className="ml-1">{patientName || 'Someone'} is typing...</span>
              </span>
            </div>
          )}

          <div className="p-3 border-t border-slate-100 flex-shrink-0">
            <div className="flex gap-2 items-end">
              <div className="flex-1 relative">
                <Textarea
                  ref={textareaRef}
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  disabled={sendingMessage}
                  className="min-h-[40px] max-h-[120px] resize-none text-sm"
                  rows={1}
                />
              </div>
              <Button
                onClick={sendMessageHandler}
                disabled={sendingMessage || !newMessage.trim()}
                className="h-10 w-10 p-0"
                size="icon"
              >
                {sendingMessage ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              Press Enter to send &bull; Shift+Enter for new line
              {isConnected && <span className="ml-2 text-emerald-600">&bull; Real-time</span>}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Send Link Dialog */}
      <Dialog open={showSendLinkDialog} onOpenChange={setShowSendLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Chat Link to Patient</DialogTitle>
            <DialogDescription>
              Send a secure link so {patientName} can access this conversation. The link expires in
              24 hours.
            </DialogDescription>
          </DialogHeader>

          {linkSent ? (
            <div className="flex flex-col items-center py-6">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <p className="font-medium text-green-600">Link Sent Successfully!</p>
              <p className="text-sm text-muted-foreground mt-1">
                The patient will receive the link via{' '}
                {sendLinkChannel === 'both' ? 'email and SMS' : sendLinkChannel}.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Send via</Label>
                  <RadioGroup
                    value={sendLinkChannel}
                    onValueChange={(v) => setSendLinkChannel(v as 'email' | 'sms' | 'both')}
                  >
                    <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="email" id="pt-msg-email" />
                      <Label
                        htmlFor="pt-msg-email"
                        className="flex items-center gap-2 cursor-pointer flex-1"
                      >
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">Email</p>
                          <p className="text-xs text-muted-foreground">
                            Send link to patient&apos;s email
                          </p>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="sms" id="pt-msg-sms" />
                      <Label
                        htmlFor="pt-msg-sms"
                        className="flex items-center gap-2 cursor-pointer flex-1"
                      >
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">SMS</p>
                          <p className="text-xs text-muted-foreground">
                            Send link via text message
                          </p>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="both" id="pt-msg-both" />
                      <Label
                        htmlFor="pt-msg-both"
                        className="flex items-center gap-2 cursor-pointer flex-1"
                      >
                        <div className="flex -space-x-1">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <Phone className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">Both</p>
                          <p className="text-xs text-muted-foreground">Send via email and SMS</p>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSendLinkDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={sendLinkToPatient} disabled={sendingLink}>
                  {sendingLink ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4 mr-2" />
                      Send Link
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatientMessagingTab;
